import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { FilterMatchMode } from "primereact/api";
import { PlotMouseEvent } from "plotly.js";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import {
    selectDefects,
    selectDefectsLoading,
    selectRegimeMappings,
    selectRegimeMappingsError,
    selectRegimeMappingsLoaded,
    selectRegimeMappingsLoading
} from "../../store/selectors/defectSelectors";
import { fetchRegimeMappings } from "../../store/thunks/defectThunks";
import { resetTableFilters } from "../../store/thunks/filtersThunks";
import { setActiveTab } from "../../store/slices/defectSlice";
import { DefectTabs } from "../../types/DefectTabs";
import LOCAL_TABLE_CONFIG_KEY from "../../types/TableLocalConfigKeys";
import { defaultDefectsFiltersConfig } from "../DefectsTable/Filters/FilterTemplates";
import { DefectTableFields } from "../DefectsTable/DefectTableFields";
import {
    buildPscRiskIndicators,
    MAX_DASHBOARD_PSC_RISKS
} from "../../utils/PscRiskGraphHelper";
import { PscRiskGraph } from "../PscRiskTab/PscRiskGraph";
import { selectUserHasCapability } from "../../store/selectors/userSelectors";
import { Capability } from "../../types/Capability";
import { sendGAEvent } from "../../AnalyticsService";
import {
    DASHBOARD_CATEGORY,
    DASHBOARD_PSC_RISK_BAR_CLICK,
    DASHBOARD_PSC_RISK_DETAILED_VIEW_CLICK
} from "../../constants/analytics";

/**
 * Counts unfiltered defects, unlike the Defects tab, to match the tiles beside
 * it - the metrics API is requested without the global filter. So the two
 * charts disagree whenever one is set.
 */
export const PscRiskTiles = () => {
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const defects = useAppSelector(selectDefects);
    const mappings = useAppSelector(selectRegimeMappings);
    const mappingsLoading = useAppSelector(selectRegimeMappingsLoading);
    const loaded = useAppSelector(selectRegimeMappingsLoaded);
    const error = useAppSelector(selectRegimeMappingsError);
    const defectsLoading = useAppSelector(selectDefectsLoading);
    const canViewDefects = useAppSelector((state) =>
        selectUserHasCapability(state, Capability.CAP_FLEETAPP_VIEW_DEFECTS)
    );

    // Defects arrive after the mappings, so waiting on the mappings alone shows
    // "no data" before the bars. Without the capability fetchDefects never runs,
    // and defect.loading, which starts true, would spin for ever.
    const loading = mappingsLoading || (canViewDefects && defectsLoading);

    // Only covers the fetch order not having run - it loads these on every
    // route. Gated on `loaded` because an empty result is a valid answer.
    useEffect(() => {
        if (!loaded && !mappingsLoading) {
            dispatch(fetchRegimeMappings());
        }
    }, [loaded, mappingsLoading, dispatch]);

    const risks = useMemo(
        () =>
            buildPscRiskIndicators(
                defects,
                mappings,
                MAX_DASHBOARD_PSC_RISKS
            ),
        [defects, mappings]
    );

    const onViewMoreClick = useCallback(() => {
        sendGAEvent(DASHBOARD_CATEGORY, DASHBOARD_PSC_RISK_DETAILED_VIEW_CLICK);
        navigate("/defects");
        dispatch(setActiveTab(DefectTabs.pscRisk));
    }, [dispatch, navigate]);

    const onBarClick = useCallback(
        (event: Readonly<PlotMouseEvent>) => {
            sendGAEvent(DASHBOARD_CATEGORY, DASHBOARD_PSC_RISK_BAR_CLICK);

            if (!event.points.length) {
                return;
            }

            const { data, pointIndex } = event.points[0];
            // The table filters on Idwal codes, not the PSC code.
            const idwalCodes = data.customdata?.[pointIndex];

            if (!Array.isArray(idwalCodes) || !idwalCodes.length) {
                return;
            }

            const tableFilters = {
                ...defaultDefectsFiltersConfig,
                [DefectTableFields.STATUS]: {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                [DefectTableFields.TYPE_CODE]: {
                    value: idwalCodes,
                    matchMode: FilterMatchMode.IN
                }
            };

            dispatch(
                resetTableFilters(LOCAL_TABLE_CONFIG_KEY.DEFECTS, tableFilters)
            );

            navigate("/defects");
            dispatch(setActiveTab(DefectTabs.defects));
        },
        [dispatch, navigate]
    );

    return (
        <div className="col-12">
            <div className="metric-card p-4 pt-3">
                {/*
                 * relative z-1 is load bearing: the Most Common Defects chart
                 * overflows its card across this header and, though nothing is
                 * drawn there, wins the hit test - so the info icon never gets
                 * mouseenter. A positioned element paints above a static one.
                 */}
                <div className="flex align-items-center relative z-1">
                    <h2>{t("dashboard.pscRiskIndicators")}</h2>
                    <div className="flex-grow-1" />
                    <p>{t("dashboard.fromLatestInspections")}</p>
                    {/* psc-risk-tooltip escapes the global nowrap rule - see App.css */}
                    <Tooltip
                        target=".dashboard-psc-risk-info"
                        className="psc-risk-tooltip"
                        position="left"
                    />
                    <i
                        className="pi pi-info-circle dashboard-psc-risk-info ml-2 mr-2"
                        data-pr-tooltip={t("defectsGraph.pscRiskInfo")}
                    />
                    <Button
                        label={t("dashboard.viewMore")}
                        text
                        onClick={onViewMoreClick}
                    />
                </div>
                <PscRiskGraph
                    className="defect-insight-graph-small"
                    risks={risks}
                    loading={loading}
                    error={error}
                    onClick={onBarClick}
                    emptyMessage={t("dashboard.noPscRiskDataAvailable")}
                />
            </div>
        </div>
    );
};
